import React, { useMemo, useState } from "react";
import { Button, DatePicker, Input, Select, Tooltip } from "antd";
import { CopyOutlined, EditOutlined, PlusOutlined, SearchOutlined, SettingOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import CustomTable from "components/CustomTable";
import StateSwitch from "components/StateSwitch";
import {
  BANNER_SCREEN_LABELS,
  BANNER_SOURCE,
  BANNER_SOURCE_LABELS,
  BANNER_TYPE_LABELS,
  TIME_STATUS,
  TIME_STATUS_LABELS,
} from "../../../core/constants";
import { formatDateRangeDisplay, formatNavigationSummary, getScreenDisplayNames } from "../../../core/utils";
import { useSearchBanners, useSwitchBannerStatus } from "../../../data/hooks";
import BannerTypeTag from "../../components/BannerTypeTag";
import BannerTimeStatusDot from "../../components/BannerTimeStatusDot";
import CreateBannerTypeModal from "../../components/CreateBannerTypeModal";
import "../../styles/marketingBanner.scss";

const { RangePicker } = DatePicker;
const { Option } = Select;

function BannerListPage() {
  const history = useHistory();

  // Filter state
  const [activeTimeStatus, setActiveTimeStatus] = useState(TIME_STATUS.ALL);
  const [searchTerm, setSearchTerm] = useState("");
  const [timeFilter, setTimeFilter] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedScreens, setSelectedScreens] = useState([]);
  const [selectedIsActive, setSelectedIsActive] = useState(undefined);

  // Pagination state
  const [pagination, setPagination] = useState({ offset: 0, limit: 10 });

  // Modal create type
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Switch status mutation
  const { switchStatus, loading: switchingStatus } = useSwitchBannerStatus();

  // Combine filters for search hook
  const filters = useMemo(() => {
    return {
      timeStatus: activeTimeStatus,
      query: searchTerm,
      timeFilter,
      types: selectedTypes.length > 0 ? selectedTypes : undefined,
      sources: selectedSources.length > 0 ? selectedSources : undefined,
      screens: selectedScreens.length > 0 ? selectedScreens : undefined,
      isActive: selectedIsActive,
    };
  }, [activeTimeStatus, searchTerm, timeFilter, selectedTypes, selectedSources, selectedScreens, selectedIsActive]);

  const { banners, paginationData, loading } = useSearchBanners(filters, pagination);

  // Handle table pagination change
  const handleTableChange = (pag) => {
    setPagination({
      offset: (pag.current - 1) * pag.pageSize,
      limit: pag.pageSize,
    });
  };

  const handleCreateTypeSelected = (chosenType) => {
    setShowCreateModal(false);
    history.push(`/marketing-banner/create?type=${chosenType}`);
  };

  const handleDuplicate = (record) => {
    history.push(`/marketing-banner/create?type=${record.type}&duplicateId=${record.id}`);
  };

  const columns = [
    {
      title: "STT",
      key: "index",
      width: 60,
      align: "center",
      render: (_, __, idx) => pagination.offset + idx + 1,
    },
    {
      title: "TÊN CÔNG CỤ",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span
          onClick={() => history.push(`/marketing-banner/detail/${record.id}`)}
          style={{ fontWeight: 600, color: "#2246dc", cursor: "pointer" }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "THỜI GIAN DIỄN RA",
      key: "timeRange",
      width: 200,
      render: (_, record) => formatDateRangeDisplay(record.startTime, record.endTime),
    },
    {
      title: "LINK ĐIỀU HƯỚNG",
      key: "navigation",
      width: 180,
      ellipsis: true,
      render: (_, record) => (
        <Tooltip title={formatNavigationSummary(record.navigation)}>
          <span>{formatNavigationSummary(record.navigation)}</span>
        </Tooltip>
      ),
    },
    {
      title: "NGUỒN",
      dataIndex: "source",
      key: "source",
      width: 140,
      render: (source) => (
        <span style={{ color: source === BANNER_SOURCE.PROMOTION ? "#2246dc" : "#595959" }}>
          {BANNER_SOURCE_LABELS[source] || source}
        </span>
      ),
    },
    {
      title: "LOẠI CÔNG CỤ",
      dataIndex: "type",
      key: "type",
      width: 150,
      align: "center",
      render: (type) => <BannerTypeTag type={type} />,
    },
    {
      title: "MÀN HÌNH HIỂN THỊ",
      key: "screens",
      width: 180,
      ellipsis: true,
      render: (_, record) => {
        const text = getScreenDisplayNames(record.screens || [], BANNER_SCREEN_LABELS);
        return (
          <Tooltip title={text}>
            <span>{text}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "TRẠNG THÁI",
      dataIndex: "timeStatus",
      key: "timeStatus",
      width: 140,
      render: (status) => <BannerTimeStatusDot status={status} />,
    },
    {
      title: "HÀNH ĐỘNG",
      key: "actions",
      width: 130,
      align: "center",
      render: (_, record) => (
        <div className="table-actions-cell" style={{ justifyContent: "center" }}>
          <Tooltip title="Chỉnh sửa">
            <EditOutlined className="action-icon" onClick={() => history.push(`/marketing-banner/edit/${record.id}`)} />
          </Tooltip>

          <Tooltip title={record.isActive ? "Tắt" : "Bật"}>
            <StateSwitch
              size="small"
              checked={record.isActive}
              loading={switchingStatus}
              onChange={(checked) => switchStatus(record.id, checked)}
            />
          </Tooltip>

          <Tooltip title="Nhân bản">
            <CopyOutlined className="action-icon" onClick={() => handleDuplicate(record)} />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="marketing-banner-container">
      {/* Page Header */}
      <div className="banner-page-header">
        <h1 className="banner-page-title">Quản lý công cụ truyền thông</h1>
      </div>

      {/* Time Status Tabs */}
      <div className="banner-time-status-tabs">
        {Object.entries(TIME_STATUS_LABELS).map(([key, label]) => {
          const isActive = activeTimeStatus === key;
          return (
            <button
              key={key}
              type="button"
              className={`tab-btn ${isActive ? "active" : ""}`}
              onClick={() => {
                setActiveTimeStatus(key);
                setPagination((prev) => ({ ...prev, offset: 0 }));
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="banner-toolbar-card">
        <div className="toolbar-filters">
          <RangePicker
            placeholder={["Từ ngày", "Đến ngày"]}
            value={timeFilter}
            onChange={(dates) => {
              setTimeFilter(dates);
              setPagination((prev) => ({ ...prev, offset: 0 }));
            }}
            style={{ width: "240px", borderRadius: "6px" }}
          />

          <Input
            placeholder="Tìm kiếm theo tên công cụ truyền thông"
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPagination((prev) => ({ ...prev, offset: 0 }));
            }}
            style={{ width: "320px", borderRadius: "6px" }}
            allowClear
          />

          <Select
            placeholder="Loại công cụ"
            allowClear
            style={{ width: "160px" }}
            value={selectedTypes[0] || undefined}
            onChange={(val) => {
              setSelectedTypes(val ? [val] : []);
              setPagination((prev) => ({ ...prev, offset: 0 }));
            }}
          >
            {Object.entries(BANNER_TYPE_LABELS).map(([k, v]) => (
              <Option key={k} value={k}>
                {v}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Nguồn"
            allowClear
            style={{ width: "140px" }}
            value={selectedSources[0] || undefined}
            onChange={(val) => {
              setSelectedSources(val ? [val] : []);
              setPagination((prev) => ({ ...prev, offset: 0 }));
            }}
          >
            {Object.entries(BANNER_SOURCE_LABELS).map(([k, v]) => (
              <Option key={k} value={k}>
                {v}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Màn hình"
            allowClear
            style={{ width: "170px" }}
            value={selectedScreens[0] || undefined}
            onChange={(val) => {
              setSelectedScreens(val ? [val] : []);
              setPagination((prev) => ({ ...prev, offset: 0 }));
            }}
          >
            {Object.entries(BANNER_SCREEN_LABELS).map(([k, v]) => (
              <Option key={k} value={k}>
                {v}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Bật / Tắt"
            allowClear
            style={{ width: "120px" }}
            value={selectedIsActive}
            onChange={(val) => {
              setSelectedIsActive(val);
              setPagination((prev) => ({ ...prev, offset: 0 }));
            }}
          >
            <Option value={true}>Đang bật</Option>
            <Option value={false}>Đang tắt</Option>
          </Select>
        </div>

        <div className="toolbar-actions">
          <Button
            icon={<SettingOutlined />}
            onClick={() => history.push("/marketing-banner/queue")}
            style={{
              borderColor: "#2246dc",
              color: "#2246dc",
              borderRadius: "6px",
              fontWeight: 500,
            }}
          >
            Cấu hình vị trí/trạng thái
          </Button>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowCreateModal(true)}
            style={{
              backgroundColor: "#2246dc",
              borderColor: "#2246dc",
              borderRadius: "6px",
              fontWeight: 500,
            }}
          >
            Tạo mới
          </Button>
        </div>
      </div>

      {/* Main Table */}
      <div className="banner-table-card">
        <CustomTable
          rowKey="id"
          dataSource={banners}
          columns={columns}
          loading={loading}
          onChange={handleTableChange}
          pagination={{
            current: Math.floor(paginationData.offset / paginationData.limit) + 1,
            pageSize: paginationData.limit,
            total: paginationData.total,
          }}
        />
      </div>

      {/* Create Type Selector Modal */}
      <CreateBannerTypeModal
        visible={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        onConfirm={handleCreateTypeSelected}
      />
    </div>
  );
}

export default BannerListPage;
